<template>
  <div class="mall">
    <div
      v-if="opsNotice"
      class="mall__ops"
      role="status"
    >
      <div class="mall__ops-inner">
        <AppIcon icon="mdi-bullhorn-outline" :size="18" />
        <p>{{ opsNotice }}</p>
        <button type="button" class="mall__ops-close" aria-label="공지 닫기" @click="dismissOpsNotice">
          <AppIcon icon="mdi-close" :size="16" />
        </button>
      </div>
    </div>

    <section v-if="showHero && hero" class="mall__deal">
      <div class="mall__deal-inner">
        <router-link :to="mallProductPath(hero.id)" class="mall__deal-banner">
          <div class="mall__deal-copy">
            <span class="mall__deal-eyebrow">
              <AppIcon icon="mdi-lightning-bolt" :size="16" />
              이달의 특가
            </span>
            <h1 class="mall__deal-title">{{ hero.name }}</h1>
            <p v-if="hero.summary" class="mall__deal-desc">{{ hero.summary }}</p>
            <div class="mall__deal-price">
              <strong>{{ formatPrice(heroPriced.unitPrice) }}원</strong>
            </div>
            <span class="mall__deal-cta">상품 보러가기</span>
          </div>
          <div class="mall__deal-visual">
            <img :src="hero.image" :alt="hero.name" />
          </div>
        </router-link>
      </div>
    </section>

    <section
      v-if="showHero && promoHtml"
      class="mall__promo"
      aria-label="이벤트 안내"
    >
      <div class="mall__promo-inner" v-html="promoHtml" />
    </section>

    <main id="mall-catalog" class="mall__main">
      <header v-if="isBrowsingFiltered" class="mall__result-head">
        <h1 class="mall__result-title">
          <template v-if="keyword">'<em>{{ keyword }}</em>' 검색결과</template>
          <template v-else-if="category !== '전체'">{{ category }}</template>
        </h1>
      </header>

      <div v-else-if="showHero" class="mall__section-head mall__catalog-head">
        <h2>전체상품</h2>
      </div>

      <div class="mall__toolbar">
        <span class="mall__count">
          <strong v-if="category !== '전체'">{{ category }}</strong>
          총 {{ filteredProducts.length }}개
        </span>
        <div class="mall__toolbar-right">
          <div class="mall__density" role="group" aria-label="목록 보기">
            <button
              type="button"
              class="mall__density-btn"
              :class="{ 'is-active': density === 'comfort' }"
              aria-label="기본"
              @click="density = 'comfort'"
            >
              <AppIcon icon="mdi-view-grid-outline" :size="18" />
            </button>
            <button
              type="button"
              class="mall__density-btn"
              :class="{ 'is-active': density === 'compact' }"
              aria-label="작게"
              @click="density = 'compact'"
            >
              <AppIcon icon="mdi-view-comfy-outline" :size="18" />
            </button>
          </div>
          <label class="mall__stock">
            <input v-model="wishOnly" type="checkbox" />
            찜한 상품
          </label>
          <label class="mall__stock">
            <input v-model="inStockOnly" type="checkbox" />
            재고있음
          </label>
        </div>
      </div>

      <div class="mall__sort-tabs" role="tablist" aria-label="정렬">
        <button
          v-for="option in sortOptions"
          :key="option.key"
          type="button"
          role="tab"
          class="mall__sort-tab"
          :class="{ 'is-active': sortKey === option.key }"
          :aria-selected="sortKey === option.key"
          @click="sortKey = option.key"
        >
          {{ option.label }}
        </button>
      </div>

      <div
        v-if="loading"
        :key="'mall-skel'"
        class="mall__grid mall__grid--skel"
        aria-busy="true"
        aria-label="상품 불러오는 중"
      >
        <span v-for="n in 8" :key="n" class="mall__skel-card" />
      </div>

      <div
        v-else-if="pagedProducts.length"
        :key="'mall-grid'"
        class="mall__grid"
        :class="{ 'mall__grid--compact': density === 'compact' }"
      >
        <ProductCard v-for="product in pagedProducts" :key="product.id" :product="product" />
      </div>

      <div
        v-else-if="keyword && !loading"
        :key="'mall-empty-search'"
        class="mall__empty-search"
      >
        <p class="mall__empty">
          '<em>{{ keyword }}</em>' 검색 결과가 없습니다.
        </p>

        <section v-if="recentSearches.length" class="mall__empty-block">
          <h2>최근 검색어</h2>
          <div class="mall__empty-chips">
            <button
              v-for="item in recentSearches"
              :key="item"
              type="button"
              class="mall__empty-chip"
              @click="goSearch(item)"
            >
              {{ item }}
            </button>
          </div>
        </section>

        <section v-if="popularFallback.length" class="mall__empty-block">
          <h2>인기상품</h2>
          <div class="mall__empty-grid">
            <ProductCard
              v-for="product in popularFallback"
              :key="`pop-${product.id}`"
              :product="product"
            />
          </div>
        </section>
      </div>

      <div v-else-if="!loading && wishOnly" :key="'mall-empty-wish'" class="mall__empty">
        <img class="mall__empty-art" :src="illust.empty" alt="" />
        <p>찜한 상품이 없습니다.</p>
      </div>

      <div v-else-if="!loading" :key="'mall-empty'" class="mall__empty">
        <img class="mall__empty-art" :src="illust.empty" alt="" />
        <p>조건에 맞는 상품이 없습니다.</p>
      </div>

      <nav
        v-if="!loading && totalPages > 1"
        class="mall__pager"
        aria-label="상품 목록 페이지"
      >
        <button
          type="button"
          class="mall__pager-btn"
          :disabled="page <= 1"
          @click="page = page - 1"
        >
          이전
        </button>
        <button
          v-for="n in pageNumbers"
          :key="n"
          type="button"
          class="mall__pager-btn"
          :class="{ 'is-active': n === page }"
          @click="page = n"
        >
          {{ n }}
        </button>
        <button
          type="button"
          class="mall__pager-btn"
          :disabled="page >= totalPages"
          @click="page = page + 1"
        >
          다음
        </button>
      </nav>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProductCard from '@/components/mall/ProductCard.vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { fetchMallCatalog, fetchMallHomeSections } from '@/api/mall'
import { fetchPaymentInfo } from '@/api/mallOrder'
import { useContentSnippets } from '@/composables/useContentSnippets'
import { useRecentSearches } from '@/composables/useRecentSearches'
import { useWishlist } from '@/composables/useWishlist'
import { illust } from '@/constants/illust'
import { MALL_HOME_PATH, mallProductPath } from '@/constants/mall'
import {
  MALL_CATEGORIES,
  MALL_DEFAULT_SORT,
  MALL_SORT_OPTIONS,
  formatPrice,
  getBestProducts,
  type MallCategory,
  type MallProduct,
  type MallSortKey,
} from '@/constants/mallProducts'
import { calcMallUnitPrice } from '@/utils/mallPricing'

const OPS_DISMISS_KEY = 'mall.opsNotice.dismissed'
const DENSITY_KEY = 'portal.mall.listDensity'
const PAGE_SIZE = 20

type Density = 'comfort' | 'compact'

const route = useRoute()
const router = useRouter()
const { wishlist } = useWishlist()
const { recentSearches } = useRecentSearches()
const { loadSnippets, snippet } = useContentSnippets()

const sortOptions = MALL_SORT_OPTIONS

const sortKey = computed<MallSortKey>({
  get: () => {
    const value = typeof route.query.sort === 'string' ? route.query.sort : MALL_DEFAULT_SORT
    return MALL_SORT_OPTIONS.some((option) => option.key === value)
      ? (value as MallSortKey)
      : MALL_DEFAULT_SORT
  },
  set: (value) => {
    const query = { ...route.query }
    if (value === MALL_DEFAULT_SORT) delete query.sort
    else query.sort = value
    delete query.page
    router.replace({ path: route.path, query })
  },
})

const page = computed({
  get: () => {
    const raw = Number(route.query.page)
    return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1
  },
  set: (value: number) => {
    const query = { ...route.query }
    if (value <= 1) delete query.page
    else query.page = String(value)
    router.replace({ path: route.path, query })
    const el = document.getElementById('mall-catalog')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  },
})

const density = ref<Density>(
  (() => {
    try {
      return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfort'
    } catch {
      return 'comfort'
    }
  })(),
)

watch(density, (value) => {
  try {
    localStorage.setItem(DENSITY_KEY, value)
  } catch {
    /* ignore */
  }
})

const loading = ref(true)
const filteredProducts = ref<MallProduct[]>([])
const hero = ref<MallProduct | null>(null)
const opsNotice = ref('')
const popularFallback = ref<MallProduct[]>([])
const promoHtml = computed(() => snippet('mall.home.promo_html').trim())

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredProducts.value.length / PAGE_SIZE)),
)

const pagedProducts = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return filteredProducts.value.slice(start, start + PAGE_SIZE)
})

const pageNumbers = computed(() => {
  const total = totalPages.value
  const current = page.value
  const windowSize = 5
  let start = Math.max(1, current - Math.floor(windowSize / 2))
  let end = Math.min(total, start + windowSize - 1)
  start = Math.max(1, end - windowSize + 1)
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

const heroPriced = computed(() =>
  calcMallUnitPrice({
    listPrice: hero.value?.listPrice || 0,
    salePrice: hero.value?.price || 0,
  }),
)

function dismissOpsNotice() {
  const text = opsNotice.value
  opsNotice.value = ''
  if (!text) return
  try {
    sessionStorage.setItem(OPS_DISMISS_KEY, text)
  } catch {
    /* ignore */
  }
}

async function loadOpsNotice() {
  try {
    const payment = await fetchPaymentInfo()
    const text = String(payment.opsNotice || '').trim()
    if (!text) {
      opsNotice.value = ''
      return
    }
    const dismissed = sessionStorage.getItem(OPS_DISMISS_KEY)
    opsNotice.value = dismissed === text ? '' : text
  } catch {
    opsNotice.value = ''
  }
}

function goSearch(q: string) {
  router.push({ path: MALL_HOME_PATH, query: { q } })
}

const keyword = computed(() => (typeof route.query.q === 'string' ? route.query.q.trim() : ''))

const category = computed<'전체' | MallCategory>(() => {
  const value = typeof route.query.cat === 'string' ? route.query.cat : '전체'
  return (MALL_CATEGORIES as string[]).includes(value) ? (value as '전체' | MallCategory) : '전체'
})

const inStockOnly = computed({
  get: () => route.query.stock === '1',
  set: (on: boolean) => {
    const query = { ...route.query }
    if (on) query.stock = '1'
    else delete query.stock
    delete query.page
    router.replace({ path: route.path, query })
  },
})

const wishOnly = computed({
  get: () => route.query.wish === '1',
  set: (on: boolean) => {
    const query = { ...route.query }
    if (on) query.wish = '1'
    else delete query.wish
    delete query.page
    router.replace({ path: route.path, query })
  },
})

/** 검색·카테고리일 때 결과 헤더. 관심/재고는 목록만 좁힌다. */
const isBrowsingFiltered = computed(() => Boolean(keyword.value) || category.value !== '전체')
const showHero = computed(() => !keyword.value && category.value === '전체')

const loadCatalog = async () => {
  loading.value = true
  try {
    let products = await fetchMallCatalog({
      category: category.value,
      keyword: keyword.value,
      sort: sortKey.value,
      inStockOnly: inStockOnly.value,
    })
    if (wishOnly.value) {
      const wished = new Set(wishlist.value)
      products = products.filter((product) => wished.has(product.id))
    }
    filteredProducts.value = products
    if (page.value > Math.max(1, Math.ceil(products.length / PAGE_SIZE))) {
      page.value = 1
    }
  } finally {
    loading.value = false
  }
}

const loadHome = async () => {
  const sections = await fetchMallHomeSections()
  hero.value = sections.hero
}

watch([keyword, category, inStockOnly, wishOnly, sortKey, wishlist], loadCatalog)

onMounted(async () => {
  await Promise.all([
    loadHome(),
    loadCatalog(),
    loadOpsNotice(),
    loadSnippets(['mall.home.promo_html']),
  ])
  popularFallback.value = getBestProducts(4)
})
</script>

<style lang="scss" src="./MallPage.scss"></style>
